import { useCallback } from 'react';
import { minBy } from 'lodash';

import { GetDeviceListFromLineage } from '@/api/fe-api/view/queries/get-device-list-from-lineage';
import type { BatteryLevel } from '@/data-explorer-v2/entities/battery-level';
import { useQueryParams } from '@/hooks/router/use-query-params';
import { useMe } from '@/hooks/use-me';

import { ListTops } from '../api/queries/list-tops';
import { ListUnits } from '../api/queries/list-units';

import { DATA_EXPLORER_QUERY_PARAMS } from './constants';

interface UseDeviceParams {
  listTops: ReturnType<typeof ListTops.useQuery>;
  listUnits: ReturnType<typeof ListUnits.useQuery>;
}

export function useDevice({ listTops, listUnits }: UseDeviceParams) {
  const {
    config: { id: tenantId },
  } = useMe();
  const [{ deviceId }, queryParams] = useQueryParams(
    DATA_EXPLORER_QUERY_PARAMS,
  );

  const lineageTop = GetDeviceListFromLineage.useQuery({
    id: deviceId,
    level:
      getDemoTopLevel(tenantId) ??
      (listTops.data?.tops?.[0]?.level as BatteryLevel),
  });

  const lineageUnit = GetDeviceListFromLineage.useQuery({
    id: deviceId,
    level:
      getDemoUnitLevel(tenantId) ??
      (listUnits.data?.units?.[0]?.level as BatteryLevel),
  });

  const selectDeviceTop = useCallback(() => {
    if (!lineageTop.isSuccess) {
      return;
    }

    const lineageTopItem = minBy(lineageTop.data, (d) => d.fullFrontendId);

    if (!lineageTopItem) {
      return;
    }

    queryParams.replace({
      topId: lineageTopItem.id,
    });
  }, [lineageTop.data, lineageTop.isSuccess, queryParams]);

  const selectDeviceUnit = useCallback(() => {
    const lineageUnitItem = minBy(lineageUnit.data, (d) => d.fullFrontendId);
    if (lineageUnit.isSuccess) {
      queryParams.replace({
        unitIds: lineageUnitItem ? [lineageUnitItem.id] : undefined,
        deviceId: undefined,
      });
    }
  }, [lineageUnit.isSuccess, queryParams, lineageUnit.data]);

  return {
    selectDeviceTop,
    selectDeviceUnit,
  };
}

function getDemoTopLevel(tenantId: string) {
  return tenantId === '49' ? 'bank' : undefined;
}

function getDemoUnitLevel(tenantId: string) {
  return tenantId === '49' ? 'pack' : undefined;
}
